import { css } from "@emotion/css";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import * as sup from "superstruct";

import { ChangePasswordStruct } from "@/modules/auth/api/resources/changePassword/changePasswordStruct";
import { Form } from "@/modules/form/components/Form/Form";
import { PasswordInput } from "@/modules/form/components/PasswordInput/PasswordInput";
import {
  areEqual,
  areNotEqual,
  notEmpty,
  passwordPattern,
} from "@/modules/form/utils/structs";
import { Button } from "@/modules/theme/components/Button/Button";
import { Typography } from "@/modules/theme/components/Typography/Typography";
import { useTheme } from "@/modules/theme/hooks/useTheme";
import { Theme } from "@/modules/theme/utils/createTheme";
import { recompose } from "@/utils/structs/recompose";

import { ChangePasswordLayout } from "./ChangePasswordLayout";
import { useChangePasswordMutation } from "./useChangePasswordMutation";
const translationNamespace = "pages/ChangePasswordPage";

const changePasswordStruct = sup.object({
  oldPassword: recompose(sup.string(), notEmpty),
  newPassword: recompose(
    sup.string(),
    notEmpty,
    areNotEqual("oldPassword"),
    passwordPattern
  ),
  confirmNewPassword: recompose(
    sup.string(),
    notEmpty,
    areEqual("newPassword")
  ),
});
type FormValues = sup.Infer<typeof ChangePasswordStruct>;

export const ChangePasswordPage = () => {
  const classes = createStyles(useTheme());
  const history = useHistory();
  const { t } = useTranslation(translationNamespace);
  const changePasswordMutation = useChangePasswordMutation();

  const handleFormSubmit = ({ oldPassword, newPassword }: FormValues) => {
    changePasswordMutation.mutate({
      currentPassword: oldPassword,
      newPassword,
    });
  };

  const errorMessages = useMemo(
    () => ({
      oldPassword: {
        "string.notEmpty": t("Old password is required"),
      },
      newPassword: {
        "string.notEmpty": t("New password is required"),
        "string.passwordPattern": t(
          "Password should be at least 6 characters long, have min. 1 uppercase letter, 1 digit and 1 special character"
        ),
        "string.areNotEqual": t(
          "New password should be different than the old one"
        ),
      },
      confirmNewPassword: {
        "string.notEmpty": t("Password confirmation is required"),
        "string.areEqual": t("Passwords should match"),
      },
    }),
    [t]
  );

  return (
    <ChangePasswordLayout className={classes.root}>
      <Typography variant="heading" className={classes.heading}>
        {t("Change Password")}
      </Typography>
      <Form
        struct={changePasswordStruct}
        defaultValues={{
          oldPassword: "",
          newPassword: "",
          confirmNewPassword: "",
        }}
        onValidSubmit={handleFormSubmit}
      >
        {({ register, trigger, formState }) => {
          const newPasswordInput = register("newPassword");
          const oldPasswordInput = register("oldPassword");
          return (
            <>
              <PasswordInput
                {...oldPasswordInput}
                autoComplete="current-password"
                errorMessages={errorMessages.oldPassword}
                onChange={async (e) => {
                  await oldPasswordInput.onChange(e);
                  if (!formState.touchedFields.newPassword) return;
                  return trigger("newPassword");
                }}
                label={t("Old Password")}
                placeholder={t("Old Password")}
              />
              <PasswordInput
                {...newPasswordInput}
                onChange={async (e) => {
                  await newPasswordInput.onChange(e);
                  if (!formState.touchedFields.confirmNewPassword) return;
                  return trigger("confirmNewPassword");
                }}
                autoComplete="new-password"
                errorMessages={errorMessages.newPassword}
                label={t("New Password")}
                placeholder={t("New Password")}
              />
              <PasswordInput
                {...register("confirmNewPassword")}
                autoComplete="new-password"
                errorMessages={errorMessages.confirmNewPassword}
                label={t("Repeat New Password")}
                placeholder={t("Repeat New Password")}
              />
              <div className={classes.buttonsWrapper}>
                <Button
                  variant="secondary"
                  isDisabled={changePasswordMutation.status === "loading"}
                  onClick={() => history.goBack()}
                >
                  {t("Cancel")}
                </Button>
                <Button
                  type="submit"
                  isLoading={changePasswordMutation.status === "loading"}
                  isDisabled={changePasswordMutation.status === "loading"}
                  withShadow
                >
                  {t("Change")}
                </Button>
              </div>
            </>
          );
        }}
      </Form>
    </ChangePasswordLayout>
  );
};

function createStyles({ spacing, typography }: Theme) {
  return {
    root: css({
      minHeight: "100vh",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
    }),
    heading: css({
      marginBottom: spacing(3),
      fontWeight: typography.fontWeight.bold,
    }),
    buttonsWrapper: css({
      display: "grid",
      gridAutoFlow: "column",
      gap: spacing(2),
    }),
  };
}
